import type { MigrationContext } from './types';

export type VersionedEntity<T> = {
    safeParse: (input: unknown, ctx?: MigrationContext) => { type: 'ok'; value: T } | { type: 'err'; error: unknown };
};

export type EntityRegistry<T> = {
    name?: string;
    getEntitySchema: (entity: unknown, ctx?: MigrationContext) => VersionedEntity<T> | null;
    migrateLegacy?: (
        entity: unknown,
        ctx?: MigrationContext
    ) => { type: 'ok'; value: unknown } | { type: 'err'; error: unknown };
};

export type MigrateEntitiesResult<T> = { type: 'ok'; value: T[] } | { type: 'err'; index: number; error: unknown };

export const migrateEntities = <T>(
    entities: Array<T | unknown>,
    registry: EntityRegistry<T>,
    ctx?: MigrationContext
): MigrateEntitiesResult<T> => {
    const migrated: T[] = [];

    for (let i = 0; i < entities.length; i += 1) {
        const result = migrateEntity(entities[i], registry, ctx);
        if (result.type !== 'ok') {
            return { type: 'err', index: i, error: result.error };
        }
        migrated.push(result.value);
    }

    return { type: 'ok', value: migrated };
};

export type MigrateEntityResult<T> = { type: 'ok'; value: T } | { type: 'err'; error: unknown };

export const migrateEntity = <T>(
    entity: T | unknown,
    registry: EntityRegistry<T>,
    ctx?: MigrationContext
): MigrateEntityResult<T> => {
    if (typeof entity !== 'object' || entity === null) {
        return { type: 'err', error: `${registry.name ?? 'entity'} must be an object` };
    }

    const version = (entity as { version?: unknown }).version;
    let working = entity as unknown;
    if (version === undefined && registry.migrateLegacy) {
        const normalized = registry.migrateLegacy(working, ctx);
        if (normalized.type !== 'ok') {
            return { type: 'err', error: normalized.error };
        }
        working = normalized.value;
    }

    const resolver = registry.getEntitySchema(working, ctx);
    if (!resolver) {
        return { type: 'err', error: `Unknown ${registry.name ?? 'entity'} subtype` };
    }

    const result = resolver.safeParse(working, ctx);
    if (result.type !== 'ok') {
        return { type: 'err', error: result.error };
    }

    return { type: 'ok', value: result.value };
};
